/**
 * Year-over-year enhancement for breakdown cells
 * Matches previous year cells to current year cells and creates YOY comparison objects
 */

import { BreakdownCell } from '@/components/ui/breakdown-tables/types';

/**
 * Create a cell key for lookup (without time component)
 */
function createNonTimeKey(cell: BreakdownCell): string {
  return `${cell.column}|${cell.subColumn ?? 'null'}|${cell.row}|${cell.subRow ?? 'null'}`;
}

/**
 * Extract numeric value from a cell
 */
function extractNumericValue(value: BreakdownCell['value']): number {
  if (typeof value === 'number') {
    return value;
  }
  if (
    value &&
    typeof value === 'object' &&
    'current' in value &&
    typeof value.current === 'number'
  ) {
    return value.current;
  }
  return 0;
}

/**
 * Enhance current year cells with YOY comparison data
 * Also creates cells for combinations that only exist in previous year
 *
 * @param currentCells - Current year breakdown cells
 * @param previousCells - Previous year breakdown cells
 * @param primaryXDimension - Primary X dimension (needed to determine if TIME is on X-axis)
 * @returns Enhanced cells with YOY objects { current, previous, delta }
 */
export function enhanceWithYOY(
  currentCells: BreakdownCell[],
  previousCells: BreakdownCell[],
): BreakdownCell[] {
  // Build a lookup map for current year cells
  const currentCellMap = new Map<string, BreakdownCell>();
  for (const currentCell of currentCells) {
    const key = createNonTimeKey(currentCell);
    currentCellMap.set(key, currentCell);
  }

  // Build a lookup map for previous year cells
  const previousCellMap = new Map<string, BreakdownCell>();
  for (const prevCell of previousCells) {
    const key = createNonTimeKey(prevCell);
    previousCellMap.set(key, prevCell);
  }

  // Enhance existing current cells with YOY data
  const enhancedCells: BreakdownCell[] = currentCells.map((currentCell) => {
    const currentValue = extractNumericValue(currentCell.value);
    const currentKey = createNonTimeKey(currentCell);
    const previousCell = previousCellMap.get(currentKey);
    const previousValue = previousCell
      ? extractNumericValue(previousCell.value)
      : 0;

    // Create YOY comparison object
    return {
      ...currentCell,
      value: {
        current: currentValue,
        previous: previousValue,
        delta: currentValue - previousValue,
      },
    };
  });

  // Create cells for combinations that only exist in previous year
  // For each previous year cell, check if a corresponding current year cell exists
  // If not, create a new cell with current=0 and previous=<value>
  for (const prevCell of previousCells) {
    const nonTimeKey = createNonTimeKey(prevCell);

    // Only create if this combination doesn't already exist in current year
    if (!currentCellMap.has(nonTimeKey)) {
      const previousValue = extractNumericValue(prevCell.value);
      // Only create cell if there's actual data (not zero)
      if (previousValue !== 0) {
        // Create a new cell with the same coordinates as the previous year cell
        // (since month names are the same for both years)
        enhancedCells.push({
          column: prevCell.column,
          subColumn: prevCell.subColumn,
          row: prevCell.row,
          subRow: prevCell.subRow,
          value: {
            current: 0,
            previous: previousValue,
            delta: -previousValue,
          },
        });
      }
    }
  }

  return enhancedCells;
}
